import { takeLatest, call, put, select } from 'redux-saga/effects';
import {
  loadTasks,
  loadTasksSuccess,
  loadTasksError,
  createTaskRequest,
  updateTaskRequest,
  transitionTaskRequest,
  addNoteRequest,
  logHoursRequest,
  loadSummary,
  loadSummarySuccess,
} from './actions';
import {
  listTasks,
  createTask,
  updateTask,
  transitionTask,
  addTaskNote,
  logTaskHours,
  fetchTaskSummary,
} from '@/services/tasks';
import { getActiveShopId, getAuthUser } from '@/utils/shopSession';
import { emitToast } from '@/state/ToastContext';
import { apiErrorMessage } from '@/services/auth';

function memberName(): string {
  const user = getAuthUser();
  return user?.name || user?.username || 'A team member';
}

interface TaskListItem {
  id: number | string;
  title?: string;
  currentStage?: string;
}

function taskTitle(tasks: TaskListItem[], id: number | string): string {
  const found = (tasks ?? []).find(t => String(t.id) === String(id));
  return found?.title || `Task #${id}`;
}

function mapTask(dto: any) {
  return {
    id: dto.id,
    taskNumber: dto.taskNumber,
    title: dto.title,
    description: dto.description,
    saleId: dto.saleId,
    customerId: dto.customerId,
    customerName: dto.customerName,
    serviceId: dto.serviceId,
    items: dto.items ?? [],
    specs: dto.specs ?? {},
    currentStage: dto.currentStage,
    priority: dto.priority,
    dueDate: dto.dueDate,
    driveUrl: dto.driveUrl,
    notes: dto.notes,
    frontdeskHours: Number(dto.frontdeskHours) || 0,
    studioHours: Number(dto.studioHours) || 0,
    printingHours: Number(dto.printingHours) || 0,
    stageEnteredAt: dto.stageEnteredAt,
    createdByUserId: dto.createdByUserId,
    readyAt: dto.readyAt,
    deliveredAt: dto.deliveredAt,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

export function* handleLoadTasks(action: ReturnType<typeof loadTasks>): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) {
      yield put(loadTasksError('No active shop selected'));
      return;
    }
    const stage = action.payload?.stage && action.payload.stage !== 'all' ? action.payload.stage : undefined;
    const raw: any[] = yield call(listTasks, shopId, stage);
    yield put(loadTasksSuccess(raw.map(mapTask)));
  } catch (e: any) {
    yield put(loadTasksError(e?.message || 'Failed to load tasks'));
  }
}

export function* handleCreateTask(action: ReturnType<typeof createTaskRequest>): Generator<any, void, any> {
  const member = memberName();
  const fallbackTitle = action.payload.input.title || 'task';
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const created: { title?: string; taskNumber?: string | number } =
      yield call(createTask, shopId, action.payload.input, { silentToast: true });
    const title = created?.title || fallbackTitle;
    emitToast({
      message: `${member} created ${title}`,
      description: created?.taskNumber ? `Task #${created.taskNumber} · at Frontdesk` : undefined,
      tone: 'success',
    });
    yield put(loadTasks());
    yield put(loadSummary());
  } catch (e: any) {
    emitToast({
      message: `${member} couldn't create ${fallbackTitle}`,
      description: apiErrorMessage(e),
      tone: 'error',
    });
    yield put(loadTasksError(e?.message || 'Failed to create task'));
  }
}

export function* handleUpdateTask(action: ReturnType<typeof updateTaskRequest>): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const { id, input } = action.payload;
    yield call(updateTask, shopId, id, input);
    yield put(loadTasks());
  } catch (e: any) {
    yield put(loadTasksError(e?.message || 'Failed to update task'));
  }
}

export function* handleTransition(action: ReturnType<typeof transitionTaskRequest>): Generator<any, void, any> {
  const member = memberName();
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const { id, toStage, note, driveUrl, hours } = action.payload;
    const tasks: TaskListItem[] = yield select(
      (state: { tasks?: { tasks?: TaskListItem[] } }) => state.tasks?.tasks ?? []
    );
    const task = (tasks ?? []).find(t => String(t.id) === String(id));
    const title = task?.title || `Task #${id}`;
    const fromStage = task?.currentStage;
    yield call(transitionTask, shopId, id, { toStage, note, driveUrl, hours }, { silentToast: true });
    const deed =
      toStage === 'studio' && fromStage !== 'studio'
        ? `assigned ${title} to Studio`
        : toStage === 'ready'
          ? `marked ${title} ready`
          : toStage === 'delivered'
            ? `marked ${title} delivered`
            : `moved ${title} to ${toStage}`;
    emitToast({ message: `${member} ${deed}`, tone: 'success' });
    yield put(loadTasks());
    yield put(loadSummary());
  } catch (e: any) {
    const tasks: TaskListItem[] = yield select(
      (state: { tasks?: { tasks?: TaskListItem[] } }) => state.tasks?.tasks ?? []
    );
    const title = taskTitle(tasks, action.payload.id);
    emitToast({
      message: `${member} couldn't move ${title}`,
      description: apiErrorMessage(e),
      tone: 'error',
    });
    yield put(loadTasksError(e?.message || 'Failed to move task'));
  }
}

export function* handleAddNote(action: ReturnType<typeof addNoteRequest>): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const { id, note, driveUrl } = action.payload;
    yield call(addTaskNote, shopId, id, { note, driveUrl });
    yield put(loadTasks());
  } catch (e: any) {
    yield put(loadTasksError(e?.message || 'Failed to add note'));
  }
}

export function* handleLogHours(action: ReturnType<typeof logHoursRequest>): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const { id, team, hours, note } = action.payload;
    yield call(logTaskHours, shopId, id, { team, hours, note });
    yield put(loadTasks());
    yield put(loadSummary());
  } catch (e: any) {
    yield put(loadTasksError(e?.message || 'Failed to log hours'));
  }
}

export function* handleLoadSummary(): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const summary: any = yield call(fetchTaskSummary, shopId);
    yield put(loadSummarySuccess(summary));
  } catch (e: any) {
    // The summary endpoint is open to every shop member now; surface any
    // unexpected failure as a warning instead of taking the dashboard down.
    console.warn('Failed to load task summary', e?.message || e);
  }
}

export default function* saga() {
  yield takeLatest(loadTasks.type, handleLoadTasks);
  yield takeLatest(createTaskRequest.type, handleCreateTask);
  yield takeLatest(updateTaskRequest.type, handleUpdateTask);
  yield takeLatest(transitionTaskRequest.type, handleTransition);
  yield takeLatest(addNoteRequest.type, handleAddNote);
  yield takeLatest(logHoursRequest.type, handleLogHours);
  yield takeLatest(loadSummary.type, handleLoadSummary);
}
